import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight, Facebook, Instagram, Music } from 'lucide-react';
import { businessInfo } from '../data/mock';

const CATEGORIES = ['All', 'Parties', 'Groups', 'Arcade', 'Facility'];

export const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeCategory, setActiveCategory] = useState('All');

  // Real photos from The Br8k Room
  const galleryImages = [
    {
      id: 1,
      url: '/party-packages-photo.webp',
      title: 'Birthday Party Crew',
      category: 'Parties'
    },
    {
      id: 2,
      url: '/gallery-bachelorette.webp',
      title: 'The Last Br8k Before the Ring',
      category: 'Parties'
    },
    {
      id: 3,
      url: '/corporate-events-photo.webp',
      title: 'Team Night in the Rage Room',
      category: 'Groups'
    },
    {
      id: 4,
      url: '/gallery-volleyball.webp',
      title: 'Marquette Volleyball Team Visit',
      category: 'Groups'
    },
    {
      id: 5,
      url: '/gallery-family.webp',
      title: 'Family Fun Night',
      category: 'Facility'
    },
    {
      id: 6,
      url: '/gallery-retro.webp',
      title: 'Retro Gaming Zone',
      category: 'Arcade'
    },
    {
      id: 7,
      url: '/gallery-lounge.webp',
      title: 'Party Lounge & Snack Area',
      category: 'Facility'
    }
  ];

  const filteredImages = activeCategory === 'All'
    ? galleryImages
    : galleryImages.filter((img) => img.category === activeCategory);

  const openLightbox = (image, index) => {
    setSelectedImage(image);
    setCurrentIndex(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const nextImage = () => {
    const newIndex = (currentIndex + 1) % filteredImages.length;
    setCurrentIndex(newIndex);
    setSelectedImage(filteredImages[newIndex]);
  };

  const prevImage = () => {
    const newIndex = (currentIndex - 1 + filteredImages.length) % filteredImages.length;
    setCurrentIndex(newIndex);
    setSelectedImage(filteredImages[newIndex]);
  };

  return (
    <div className="min-h-screen pt-24 pb-12">
      {/* Hero */}
      <section className="py-20 bg-gradient-to-br from-page via-card to-accent relative">
        <div className="absolute inset-0 cyber-grid opacity-20"></div>
        <div className="container mx-auto px-6 text-center relative z-10">
          <h1 className="heading-1 text-6xl md:text-7xl lg:text-8xl font-black uppercase mb-6 leading-tight">
            <span className="text-primary" style={{ textShadow: '0 0 20px var(--neon-green)' }}>Photo </span>
            <span className="text-blue" style={{ textShadow: '0 0 20px var(--neon-blue)' }}>Gallery</span>
          </h1>
          <p className="body-large text-white max-w-3xl mx-auto">
            See the action! Check out our VR experiences, facility, and happy customers.
          </p>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16 bg-page relative">
        <div className="absolute inset-0 cyber-grid opacity-10"></div>
        <div className="container mx-auto px-6 relative z-10">
          {/* Category filter tabs */}
          <div className="flex flex-wrap justify-center gap-3 mb-12" data-testid="gallery-category-filters">
            {CATEGORIES.map((cat) => {
              const isActive = activeCategory === cat;
              return (
                <button
                  key={cat}
                  data-testid={`gallery-filter-${cat.toLowerCase()}`}
                  onClick={() => { setActiveCategory(cat); setSelectedImage(null); }}
                  className={`px-6 py-2.5 rounded-full border-2 text-sm font-black uppercase tracking-wider transition-all duration-300 ${
                    isActive ? 'text-inverse bg-primary border-green' : 'text-primary bg-card border-green/40 hover:border-green'
                  }`}
                  style={{
                    boxShadow: isActive
                      ? '0 0 20px rgba(0, 255, 65, 0.6)'
                      : '0 0 8px rgba(0, 255, 65, 0.15)',
                  }}
                >
                  {cat}
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredImages.map((image, index) => (
              <div
                key={image.id}
                className="group relative aspect-video bg-card border-2 border-green rounded-2xl overflow-hidden cursor-pointer hover:scale-105 transition-all duration-300"
                style={{ boxShadow: '0 0 20px rgba(0, 255, 65, 0.2)' }}
                onClick={() => openLightbox(image, index)}
              >
                <img
                  src={image.url}
                  alt={image.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-6">
                  <div>
                    <span className="inline-block px-3 py-1 bg-primary text-inverse text-xs font-bold uppercase rounded-full mb-2">
                      {image.category}
                    </span>
                    <h3 className="text-white font-bold text-lg">{image.title}</h3>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Social follow */}
          <div className="mt-12 bg-card border-2 border-blue rounded-2xl p-8 text-center" style={{ boxShadow: '0 0 20px rgba(0, 217, 255, 0.3)' }}>
            <h3 className="text-blue font-black text-2xl uppercase mb-4">More Photos Coming Soon!</h3>
            <p className="text-white mb-6">
              We're constantly adding new photos of our facility, attractions, and happy customers having an amazing time.
              Follow us for more behind-the-scenes content!
            </p>
            <div className="flex justify-center gap-4">
              <a
                href={businessInfo.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="gallery-instagram-link"
                aria-label="Instagram"
                className="w-12 h-12 rounded-full border-2 border-pink flex items-center justify-center text-pink hover:scale-110 transition-transform"
                style={{ boxShadow: '0 0 15px rgba(255, 0, 255, 0.35)' }}
              >
                <Instagram size={22} />
              </a>
              <a
                href={businessInfo.social.facebook}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="gallery-facebook-link"
                aria-label="Facebook"
                className="w-12 h-12 rounded-full border-2 border-blue flex items-center justify-center text-blue hover:scale-110 transition-transform"
                style={{ boxShadow: '0 0 15px rgba(0, 217, 255, 0.35)' }}
              >
                <Facebook size={22} />
              </a>
              <a
                href={businessInfo.social.tiktok}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="gallery-tiktok-link"
                aria-label="TikTok"
                className="w-12 h-12 rounded-full border-2 border-green flex items-center justify-center text-primary hover:scale-110 transition-transform"
                style={{ boxShadow: '0 0 15px rgba(0, 255, 65, 0.35)' }}
              >
                <Music size={22} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-95 flex items-center justify-center p-4" onClick={closeLightbox}>
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white hover:text-primary transition-colors z-10"
            aria-label="Close"
          >
            <X size={32} />
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
            className="absolute left-4 text-white hover:text-primary transition-colors z-10"
            aria-label="Previous"
          >
            <ChevronLeft size={48} />
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
            className="absolute right-4 text-white hover:text-primary transition-colors z-10"
            aria-label="Next"
          >
            <ChevronRight size={48} />
          </button>

          <div className="max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={selectedImage.url}
              alt={selectedImage.title}
              className="w-full h-auto max-h-[80vh] object-contain bg-card rounded-2xl"
            />
            <div className="mt-4 text-center">
              <span className="inline-block px-3 py-1 bg-primary text-inverse text-xs font-bold uppercase rounded-full mb-2">
                {selectedImage.category}
              </span>
              <h3 className="text-white font-bold text-xl">{selectedImage.title}</h3>
              <p className="text-secondary text-sm mt-2">
                {currentIndex + 1} / {filteredImages.length}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};